import React, { useState, useEffect } from "react";
import {
  Pencil,
  Clock,
  ArrowLeft,
  Save,
  X,
  CheckCircle,
  XCircle,
  RefreshCw,
  CreditCard,
  Globe,
  MapPin,
  Phone,
  Building,
} from "lucide-react";
import { BASE_URL, REACT_APP_RAZORPAY_KEY_ID } from "utils/constant";
import OrganizationDetailsSkeleton from "../../components/UI_components/OrganizationDetailsSkeleton";
import axios from "axios";
import LogoUploader from "components/UI_components/LogoUploader";
import { OrgAPI, SubscriptionAPI } from "api/dashboard/service";

const OrganizationDetails = ({ handleBackClick }) => {
  const [organization, setOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const convertDate = (date) => {
    const d =
      typeof date === "number" || /^\d+$/.test(date)
        ? new Date(parseInt(date) * (date.toString().length <= 10 ? 1000 : 1))
        : new Date(date);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return d.toLocaleDateString(undefined, options);
  };

  useEffect(() => {
    fetchOrganizationDetails();
    fetchSubscriptionDetails();
  }, []);

  useEffect(() => {
    if (subscription?.razorpay_subscription) {
      initializeRazorpay();
    }
  }, [subscription]);

  const fetchOrganizationDetails = async () => {
    try {
      const organization = localStorage.getItem("organization");
      const organizationId = JSON.parse(organization)?.id;

      if (!organizationId) throw new Error("Missing organization ID");

      const result = await OrgAPI.getOrganization(organizationId);
      if (result.status) {
        setOrganization(result.data);
        setEditValues(result.data);
      } else {
        throw new Error(result.message || "Failed to fetch organization details");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      const organization = localStorage.getItem("organization");
      const organizationId = JSON.parse(organization)?.id;

      if (!organizationId) throw new Error("Missing organization ID");

      const subscriptionResult = await SubscriptionAPI.checkUserSubscription(
        organizationId
      );
      if (subscriptionResult.status) {
        setSubscription(subscriptionResult.data);
      }
    } catch (err) {
      console.error("Failed to fetch subscription:", err);
    }
  };

  const initializeRazorpay = () => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  };

  const onPaymentSuccess = async (paymentData) => {
    try {
      setPaymentProcessing(true);
      const accessToken = localStorage.getItem("access");

      const validateResponse = await axios.post(
        `${BASE_URL}/subscription/validate-payment/`,
        {
          subscription_id: subscription.subscription_id,
          razorpay_payment_id: paymentData.razorpay_payment_id,
          razorpay_signature: paymentData.razorpay_signature,
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (validateResponse.data.success) {
        fetchSubscriptionDetails();
        const subscriptionDetails = validateResponse.data.subscription_details;
        localStorage.setItem(
          "subscription_details",
          JSON.stringify(subscriptionDetails)
        );
      } else {
        setError(validateResponse.data.message || "Payment validation failed");
      }
    } catch (err) {
      console.error("Payment validation error:", err);
      setError(
        err.response?.data?.error ||
        "Payment validation failed. Please contact support."
      );
    } finally {
      setPaymentProcessing(false);
    }
  };

  const openRazorpayPayment = () => {
    if (!subscription?.razorpay_subscription) {
      setError("Subscription details not available");
      return;
    }
    const userDetails = JSON.parse(localStorage.getItem("user")) || {};
    const options = {
      key: REACT_APP_RAZORPAY_KEY_ID,
      subscription_id: subscription.razorpay_subscription,
      name: organization?.organization_name || "Your Organization",
      description: subscription?.subscription_name || "Subscription Plan",
      image: "https://www.sitemaster.ai/images/logo.png",
      handler: function (response) {
        onPaymentSuccess(response);
      },
      prefill: {
        name: userDetails?.first_name + userDetails?.last_name || "",
        email: userDetails?.user_email || "",
        contact: organization?.contact_number || "",
      },
      notes: {
        organization_id: organization?.id,
      },
      theme: {
        color: "#F37254",
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setUpdateError(null);
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValues(organization);
    setUpdateError(null);
  };

  const handleUpdate = async (field) => {
    try {
      const organizationId = organization.id;
      const updatePayload = {
        organization_name: editValues.organization_name,
        business_address: editValues.business_address,
        pincode: parseInt(editValues.pincode),
        state: editValues.state,
        contact_number: editValues.contact_number,
        website: editValues.website,
      };

      const result = await OrgAPI.updateOrganization(
        organizationId,
        updatePayload,
        "PUT"
      );
      if (result.status) {
        setOrganization(editValues);
        setEditingField(null);
        setUpdateError(null);
      } else {
        throw new Error(result.message || "Failed to update organization");
      }
    } catch (err) {
      setUpdateError(err.message);
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [field]: field === "pincode" ? value.replace(/\D/g, "") : value,
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event;
    if (!file) return;
    const formData = new FormData();
    formData.append("logo_image_file", file);
    const organizationId = organization.id;
    try {
      const result = await OrgAPI.updateOrganization(
        organizationId,
        formData,
        "PATCH"
      );
      if (result.status) {
        setOrganization((prev) => ({
          ...prev,
          logo_image_file: result.data.logo_image_file,
        }));
        setEditValues((prev) => ({
          ...prev,
          logo_image_file: result.data.logo_image_file,
        }));
        return result.data.logo_image_file;
      } else {
        throw new Error(result.message || "Failed to upload logo");
      }
    } catch (err) {
      setUpdateError(err.message);
      throw err;
    }
  };

  const isSubscriptionActive = () =>
    subscription?.active_flag &&
    subscription?.valid_till > new Date().toISOString();

  const isSubscriptionPending = () =>
    subscription?.razorpay_subscription && !subscription.active_flag;

  if (loading) return <OrganizationDetailsSkeleton />;

  const detailFields = [
    {
      label: "Organization Name",
      field: "organization_name",
      icon: Building,
      type: "text",
    },
    {
      label: "Contact Number",
      field: "contact_number",
      icon: Phone,
      type: "text",
    },
    {
      label: "Website",
      field: "website",
      icon: Globe,
      type: "text",
    },
    {
      label: "State",
      field: "state",
      icon: MapPin,
      type: "text",
    },
    {
      label: "Pincode",
      field: "pincode",
      icon: MapPin,
      type: "number",
    },
    {
      label: "Business Address",
      field: "business_address",
      icon: MapPin,
      type: "text",
      fullWidth: true,
    },
  ];

  return (
    <div className="p-4 w-[85%] mx-auto h-[90vh] overflow-y-auto scrollbar-none space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <button
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          onClick={handleBackClick}
        >
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <h1 className="text-lg font-medium text-gray-800">Organization Settings</h1>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row items-start md:items-center gap-6">
        <LogoUploader
          organization={organization}
          onUpload={handleImageUpload}
        />

        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-bold text-gray-900">
              {organization?.organization_name}
            </h2>
            {isSubscriptionActive() ? (
              <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200">
                Active Plan
              </span>
            ) : isSubscriptionPending() ? (
              <span className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full border border-yellow-200">
                Payment Pending
              </span>
            ) : (
              <span className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full border border-red-200">
                Inactive
              </span>
            )}
          </div>

          <div className="flex items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <span>
                Valid till:{" "}
                <span className="font-medium text-gray-900">
                  {subscription?.razorpay_subscription
                    ? convertDate(subscription.valid_till)
                    : "N/A"}
                </span>
              </span>
            </div>
            <div>
              Active Projects:{" "}
              <span className="font-medium text-gray-900">
                {subscription?.active_projects || 0}
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowSubscriptionDetails(!showSubscriptionDetails)}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline whitespace-nowrap"
        >
          {showSubscriptionDetails ? "Hide Details" : "View Subscription"}
        </button>
      </div>

      {/* Subscription Details Panel */}
      {showSubscriptionDetails && subscription && (
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-100 animate-in fade-in slide-in-from-top-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-900">
              Subscription Details
            </h3>
            {isSubscriptionPending() && (
              <button
                onClick={openRazorpayPayment}
                disabled={paymentProcessing}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors shadow-sm"
              >
                {paymentProcessing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                {paymentProcessing ? "Processing..." : "Complete Payment"}
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard label="Plan" value={subscription.subscription_name} />
            <InfoCard
              label="Status"
              value={isSubscriptionActive() ? "Active" : "Inactive"}
              valueClass={isSubscriptionActive() ? "text-green-600" : "text-red-600"}
            />
            <InfoCard
              label="Start Date"
              value={subscription.active_flag ? convertDate(subscription.update_time) : "N/A"}
            />
            <InfoCard
              label="End Date"
              value={subscription.valid_till ? convertDate(subscription.valid_till) : "N/A"}
            />
            <InfoCard
              label="Auto Renewal"
              value={subscription.auto_renewable ? "Enabled" : "Disabled"}
            />
            <InfoCard
              label="Active Members"
              value={subscription.active_members || 0}
            />
          </div>
        </div>
      )}

      {/* Organization Details Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Organization Details</h3>
          {updateError && (
            <span className="text-sm text-red-600 bg-red-50 px-3 py-1 rounded-full">
              {updateError}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {detailFields.map((item) => (
            <div
              key={item.field}
              className={`bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow ${item.fullWidth ? 'md:col-span-2' : ''}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </div>

                  {editingField === item.field ? (
                    <div className="mt-2">
                      <input
                        type={item.type}
                        value={editValues[item.field] || ""}
                        onChange={(e) => handleInputChange(item.field, e.target.value)}
                        className="w-full p-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50 text-gray-900"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <p className="text-gray-900 font-medium mt-1 break-words">
                      {organization[item.field] || <span className="text-gray-400 italic">Not set</span>}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {editingField === item.field ? (
                    <>
                      <button
                        onClick={() => handleUpdate(item.field)}
                        className="p-2 hover:bg-green-50 text-green-600 rounded-lg transition-colors"
                        title="Save"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEdit(item.field)}
                      className="p-2 hover:bg-gray-100 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ label, value, valueClass = "text-gray-900" }) => (
  <div className="bg-white/50 p-3 rounded-lg">
    <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
    <p className={`font-medium mt-1 ${valueClass}`}>{value || "N/A"}</p>
  </div>
);

export default OrganizationDetails;
